import type { AwardCheck, Me } from '@myrewards/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { takeOverClaim } from '../../api/ops';
import { Button, ConfirmDialog, useToast } from '../../ui';
import { problemWords } from '../labels';
import { useClaimChanged } from '../queue/ClaimButton';
import { Notice } from '../shared';
import s from '../Steps.module.css';

type Holder = NonNullable<AwardCheck['claimedBy']>;

/**
 * Letters someone else holds: who holds them and, for a Stock Admin, taking them over with a reason
 * (APP-271, Tom on FB-2026-0033). While acting as someone, the holder can be the tester's own login,
 * which reads as a teammate's claim and looks broken, so it says so.
 */
export function HeldClaim({
  awardId,
  check,
  displayId,
  holder,
  me,
}: {
  awardId: string;
  check: AwardCheck;
  displayId: string;
  holder: Holder;
  me: Me;
}): ReactElement {
  const toast = useToast();
  const changed = useClaimChanged();
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const take = useMutation({
    mutationFn: (reason: string) => takeOverClaim(awardId, check.award.version, reason),
    onSuccess: async () => {
      setError(null);
      await changed(awardId);
      toast.show({
        tone: 'done',
        title: `You’re on ${displayId}`,
        body: 'Your claim shows to the whole team, and your reason is in the award’s history.',
      });
    },
    onError: async (e) => {
      setError(problemWords(e));
      await changed(awardId);
    },
  });
  const canTake = check.actions.takeOver;
  const ownLogin = me.actingAs?.signedInAs.id === holder.id;
  const reader = me.person.displayName;
  return (
    <div className={s.claimRow}>
      <Notice tone="waiting">
        {ownLogin
          ? `Your own login holds this claim, and you’re previewing as ${reader}. To make or release these letters, exit Preview as${canTake ? `, or take the claim over as ${reader}` : ''}.`
          : `${holder.name} is preparing these letters. You can read them here; only they can generate or release them.${canTake ? ' If they can’t get to it, you can take it over.' : ''}`}
      </Notice>
      {canTake && (
        <Button
          variant="navy"
          size="sm"
          onPress={() => {
            setError(null);
            setAsking(true);
          }}
        >
          Take over
        </Button>
      )}
      <ConfirmDialog
        isOpen={asking}
        onOpenChange={setAsking}
        title={`Take over ${displayId}?`}
        intro="You’ll hold its letters, and only you can make or release them. The whole team sees that you hold them."
        summary={[
          { label: 'Award', value: displayId },
          { label: 'Held by', value: holder.name },
        ]}
        reasonLabel="Why are you taking it over?"
        reasonDescription="It goes in the award’s history, where anyone who can open the award can read it, including the person who entered it."
        reasonRequired
        confirmLabel="Take over the claim"
        error={error}
        onConfirm={async (reason) => {
          try {
            await take.mutateAsync(reason);
            return true;
          } catch {
            return false;
          }
        }}
      />
    </div>
  );
}
